/**
 * IFRAP-AI - Report Generator Page (Back-office)
 * Génère des rapports d'analyse IFRAP à partir d'un simple prompt.
 */

import React, { useState, useEffect } from 'react';
import { FileText, Send, Loader2, Mail, Clock, Download, ChevronDown, ChevronUp, Presentation } from 'lucide-react';
import api from '../../api/client';

const EXAMPLE_PROMPTS = [
  "Analyse du coût des 35 heures sur l'économie française",
  "Impact de la hausse des dépenses publiques sur la compétitivité",
  "Comparaison France-Allemagne sur les retraites",
  "Le déficit de la Sécurité sociale : causes et solutions",
  "Réforme de la fonction publique territoriale",
  "Coût des normes pour les entreprises françaises",
];

const ReportGeneratorPage = () => {
  const [prompt, setPrompt] = useState('');
  const [contentText, setContentText] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [exportGamma, setExportGamma] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [archives, setArchives] = useState([]);
  const [expandedArchive, setExpandedArchive] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');
  const [mode, setMode] = useState('generate'); // 'generate' | 'transform'

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      const token = localStorage.getItem('ifrap_token');
      const { data } = await api.get('/api/reports/list?limit=20', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchives(data.reports || []);
    } catch (e) {
      console.error('Error fetching archives:', e);
    }
  };

  const generateReport = async () => {
    if (loading) return;

    // Mode transform: submit content directly to Gamma
    if (mode === 'transform') {
      if (!contentText.trim()) return;
      setLoading(true);
      setReport(null);
      try {
        const token = localStorage.getItem('ifrap_token');
        const { data } = await api.post('/api/reports/transform-to-gamma', {
          content: contentText.trim(),
          title: contentTitle.trim() || undefined,
          email: sendEmail ? (emailTo || undefined) : undefined,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReport({
          title: data.title,
          content: contentText,
          gamma: data.gamma,
          date: new Date().toLocaleDateString('fr-FR'),
          word_count: contentText.split(/\s+/).length,
        });
        fetchArchives();
      } catch (e) {
        setReport({ error: e.response?.data?.detail || 'Erreur Gamma' });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Mode generate: AI generates report
    if (!prompt.trim()) return;
    setLoading(true);
    setReport(null);

    try {
      const token = localStorage.getItem('ifrap_token');
      const { data } = await api.post('/api/reports/generate', {
        prompt: prompt.trim(),
        send_email: sendEmail,
        email_to: emailTo || undefined,
        export_gamma: exportGamma,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setReport(data);
      fetchArchives();
    } catch (e) {
      console.error('Error generating report:', e);
      setReport({ error: e.response?.data?.detail || 'Erreur lors de la génération' });
    } finally {
      setLoading(false);
    }
  };

  const loadArchive = async (reportId) => {
    if (expandedArchive === reportId) {
      setExpandedArchive(null);
      return;
    }
    try {
      const token = localStorage.getItem('ifrap_token');
      const { data } = await api.get(`/api/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
      setExpandedArchive(reportId);
      setActiveTab('generate');
    } catch (e) {
      console.error('Error loading report:', e);
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.*$)/gm, '<h3 class="text-base font-bold text-[#000091] mt-5 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-lg font-bold text-[#000091] mt-6 mb-3 pb-2 border-b border-gray-200">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-xl font-bold text-[#E1000F] mt-4 mb-4">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#000091]">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 mb-1">$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 mb-1"><span class="font-bold text-[#E1000F]">$1.</span> $2</li>')
      .replace(/---/g, '<hr class="my-4 border-t-2 border-[#E1000F]">')
      .replace(/\n\n/g, '</p><p class="mb-3">')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="space-y-6" data-testid="report-generator-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#FFB000] flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Générateur de rapports IFRAP
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Génère une note d'analyse complète dans le style IFRAP à partir d'un simple sujet
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('generate')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'generate' ? 'bg-[#FFB000] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            Générer
          </button>
          <button
            onClick={() => setActiveTab('archives')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'archives' ? 'bg-[#FFB000] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            Archives ({archives.length})
          </button>
        </div>
      </div>

      {activeTab === 'generate' && (
        <>
          {/* Mode selector */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMode('generate')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'generate' ? 'bg-[#FFB000] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <FileText className="w-4 h-4" />
              Générer un rapport IA
            </button>
            <button
              onClick={() => setMode('transform')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'transform' ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Presentation className="w-4 h-4" />
              Contenu → Gamma
            </button>
          </div>

          {/* Prompt input (generate mode) */}
          {mode === 'generate' && (
          <div className="bg-[#0f0f20] rounded-xl border border-gray-800 p-5">
            <label className="text-sm font-medium text-gray-300 mb-3 block">
              Sujet du rapport
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex: Analyse du coût des 35 heures sur l'économie française..."
              className="w-full bg-[#1a1a2e] text-white border border-gray-700 rounded-lg p-4 text-sm resize-none focus:border-[#FFB000] outline-none"
              rows={3}
              disabled={loading}
              data-testid="report-prompt-input"
            />

            {/* Example prompts */}
            <div className="flex flex-wrap gap-2 mt-3">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(ex)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors border border-gray-800"
                >
                  {ex.length > 45 ? ex.slice(0, 45) + '...' : ex}
                </button>
              ))}
            </div>

            {/* Options */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-800">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="rounded"
                />
                <Mail className="w-4 h-4" />
                Envoyer par email
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportGamma}
                  onChange={(e) => setExportGamma(e.target.checked)}
                  className="rounded"
                />
                <Presentation className="w-4 h-4" />
                Présentation Gamma
              </label>
              {sendEmail && (
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Email (défaut: le vôtre)"
                  className="bg-[#1a1a2e] text-white border border-gray-700 rounded-lg px-3 py-1.5 text-sm flex-1 max-w-xs focus:border-[#FFB000] outline-none"
                />
              )}
              <button
                onClick={generateReport}
                disabled={!prompt.trim() || loading}
                className="ml-auto flex items-center gap-2 bg-[#FFB000] text-black px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-[#FFB000]/80 disabled:opacity-30 transition-colors"
                data-testid="report-generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Générer le rapport
                  </>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Content input (transform mode) */}
          {mode === 'transform' && (
          <div className="bg-[#0f0f20] rounded-xl border border-purple-800/30 p-5">
            <label className="text-sm font-medium text-purple-300 mb-3 block">
              Titre (optionnel)
            </label>
            <input
              type="text"
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              placeholder="Ex: Analyse du budget 2026"
              className="w-full bg-[#1a1a2e] text-white border border-gray-700 rounded-lg p-3 text-sm mb-4 focus:border-purple-500 outline-none"
              disabled={loading}
            />
            <label className="text-sm font-medium text-purple-300 mb-3 block">
              Collez votre contenu
            </label>
            <textarea
              value={contentText}
              onChange={(e) => setContentText(e.target.value)}
              placeholder="Collez ici votre texte, article, notes, rapport... Gamma le transformera en présentation aux couleurs IFRAP."
              className="w-full bg-[#1a1a2e] text-white border border-gray-700 rounded-lg p-4 text-sm resize-none focus:border-purple-500 outline-none"
              rows={8}
              disabled={loading}
              data-testid="transform-content-input"
            />
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-800">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="rounded"
                />
                <Mail className="w-4 h-4" />
                Envoyer le lien par email
              </label>
              <button
                onClick={generateReport}
                disabled={!contentText.trim() || loading}
                className="ml-auto flex items-center gap-2 bg-purple-600 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-purple-700 disabled:opacity-30 transition-colors"
                data-testid="transform-gamma-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Conversion Gamma...</>
                ) : (
                  <><Presentation className="w-4 h-4" /> Créer la présentation Gamma</>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="bg-[#0f0f20] rounded-xl border border-[#FFB000]/20 p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#FFB000] mx-auto mb-3" />
              <p className="text-[#FFB000] font-medium">Rédaction du rapport en cours...</p>
              <p className="text-gray-500 text-sm mt-1">L'IA analyse les données IFRAP et rédige le rapport (~30 secondes)</p>
            </div>
          )}

          {/* Report display */}
          {report && !report.error && !loading && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="report-result">
              {/* Report header */}
              <div className="bg-gradient-to-r from-[#000091] to-[#1a1a6e] px-6 py-5">
                <div className="text-white/60 text-xs uppercase tracking-widest mb-1">Fondation IFRAP — Note d'analyse</div>
                <h2 className="text-white text-lg font-bold">{report.title}</h2>
                <div className="flex items-center gap-4 mt-2 text-white/60 text-xs">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {report.date}</span>
                  <span>{report.word_count} mots</span>
                  {report.email_sent && (
                    <span className="flex items-center gap-1 text-green-300">
                      <Mail className="w-3 h-3" /> Envoyé à {report.email_to}
                    </span>
                  )}
                  {report.gamma?.success && (
                    <a
                      href={report.gamma.gamma_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-purple-300 hover:text-purple-200"
                    >
                      <Presentation className="w-3 h-3" /> Ouvrir dans Gamma
                    </a>
                  )}
                </div>
              </div>

              {/* Report content */}
              <div
                className="p-6 prose prose-sm max-w-none text-gray-800"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(report.content) }}
              />
            </div>
          )}

          {/* Error */}
          {report?.error && (
            <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 text-red-400 text-sm">
              {report.error}
            </div>
          )}
        </>
      )}

      {/* Archives tab */}
      {activeTab === 'archives' && (
        <div className="space-y-3">
          {archives.length === 0 ? (
            <div className="bg-[#0f0f20] rounded-xl border border-gray-800 p-8 text-center text-gray-500">
              Aucun rapport archivé. Générez votre premier rapport !
            </div>
          ) : (
            archives.map((r) => (
              <div
                key={r.session_id}
                className="bg-[#0f0f20] rounded-xl border border-gray-800 overflow-hidden"
              >
                <button
                  onClick={() => loadArchive(r.report_id || r.session_id)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors text-left"
                >
                  <div>
                    <p className="text-white font-medium text-sm">{r.title}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {r.date} · {r.word_count} mots · Par {r.generated_by}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <span className="text-xs bg-white/5 px-2 py-1 rounded">{r.prompt?.slice(0, 40)}...</span>
                    {expandedArchive === (r.report_id || r.session_id) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ReportGeneratorPage;
